import type { ChatChannelFilters } from "Types/Chat/chatFilters";
import { ChannelKind } from "Types/Chat/channelKind";

type Params = {
  userId: string;
  /** Trimmed, non-empty search term. */
  term: string;
  /**
   * Stream user ids of people whose LOCAL display name matches the term. Stream only knows a
   * private persona by their @handle (ADR-0007), so the real name the list shows them under can
   * only be matched here, from the disclosed-names map, and handed to the server as member ids.
   */
  matchingDisclosedIds: ReadonlyArray<string>;
};

/**
 * Server-side channel filter for a search term, so the search spans every conversation the user
 * belongs to rather than the page the list happens to have loaded.
 *
 * Word-prefix matching (`$autocomplete`) on the channel name and on the other members' disclosable
 * names, plus the DMs with locally matched disclosed names. Spans all categories: the tabs are hidden
 * while a term is typed. Two exclusions are deliberate:
 *
 * - `joined: true` — pending and rejected invites stay reachable only through the Requests tab.
 * - `frozen: false` — a frozen channel is anonymised (ADR-0008); matching the hidden counterpart's
 *   disclosable name would re-identify exactly what the list hides.
 */
export const buildChannelSearchFilters = ({
  userId,
  term,
  matchingDisclosedIds,
}: Params): ChatChannelFilters => ({
  type: "messaging",
  members: { $in: [userId] },
  joined: true,
  frozen: false,
  $or: [
    { name: { $autocomplete: term } },
    { "member.user.name": { $autocomplete: term } },
    ...(matchingDisclosedIds.length > 0
      ? [
          {
            channelKind: ChannelKind.Direct,
            members: { $in: [...matchingDisclosedIds] },
          },
        ]
      : []),
  ],
});

/**
 * People whose disclosed display name contains the term (case-insensitive substring, like the
 * list's own local matching), as Stream user ids. Empty while the disclosed-names map is still
 * loading.
 */
export const findDisclosedIdsMatchingTerm = (
  disclosedNames: ReadonlyMap<string, string> | undefined,
  term: string,
): Array<string> => {
  if (!disclosedNames?.size) {
    return [];
  }
  const needle = term.toLowerCase();
  return [...disclosedNames]
    .filter(([, displayName]) => displayName.toLowerCase().includes(needle))
    .map(([streamUserId]) => streamUserId);
};
